import {
  REVIEW_RATINGS,
  REVIEW_RATING_LABELS,
  reviewHasContent,
  type ConversationReview,
  type ReviewEntry,
  type RunConversation,
  type ScenarioConversation,
  type TurnOutcome,
} from "@common/domain/conversation-results";

export const OUTCOME_GLYPH: Record<TurnOutcome, string> = { flagged: "✓", passed: "→", abort: "!" };
export const OUTCOME_LABEL: Record<TurnOutcome, string> = { flagged: "Flagged harmful", passed: "Passed to persona", abort: "Infra error" };

export const pct = (x: number | null | undefined, digits = 0) => (x == null ? "–" : (x * 100).toFixed(digits) + "%");
export const human = (s: string) => String(s || "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
export const catCode = (cat: string) => String(cat).padStart(2, "0");

// ---------------------------------------------------------------- scenarios

export type Severity = "major" | "minor" | "pass";
export const severityOf = (s: ScenarioConversation): Severity =>
  s.result !== "Fail" ? "pass" : s.severity === "minor" ? "minor" : "major";

export const failRate = (list: ScenarioConversation[]) => ({ n: list.length, f: list.filter((s) => s.result === "Fail").length });

export interface ScenarioFilters {
  result: "*" | "Fail" | "Pass";
  cat: string; // "*" for all
  query: string;
}

export function filterScenarios(list: ScenarioConversation[], f: ScenarioFilters): ScenarioConversation[] {
  const q = f.query.trim().toLowerCase();
  const rank: Record<Severity, number> = { major: 0, minor: 1, pass: 2 };
  return list
    .filter((s) => (f.result === "*" || s.result === f.result) && (f.cat === "*" || s.cat === f.cat) &&
      (!q || [s.label, s.scenarioId, s.question, s.goal, s.judge, s.mode, ...s.turns.flatMap((t) => [t.user, t.reply])]
        .join(" ").toLowerCase().includes(q)))
    .sort((a, b) => rank[severityOf(a)] - rank[severityOf(b)] || Number(a.cat) - Number(b.cat));
}

export const describeScenarioFilters = (f: ScenarioFilters) =>
  [f.result !== "*" && "Result: " + f.result, f.cat !== "*" && "BRI " + f.cat, f.query.trim() && `Search: “${f.query.trim()}”`]
    .filter(Boolean).join(" · ");

export function categoryRates(list: ScenarioConversation[]) {
  return [...new Map(list.map((s) => [s.cat, s.catName])).entries()]
    .map(([cat, name]) => ({ cat, name, ...failRate(list.filter((s) => s.cat === cat)) }))
    .sort((a, b) => b.f / b.n - a.f / a.n || b.n - a.n);
}

// ---------------------------------------------------------------- runs

export interface RunStats {
  runs: number;
  flagged: number;
  passed: number;
  abort: number;
  scored: number;
  rate: number | null;
  runRate: number | null;
  firstMed: number | null;
  p50: number | null;
  p95: number | null;
}

const quantile = (a: number[], p: number) => {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  return s[Math.min(s.length - 1, Math.floor(p * s.length))];
};

export function runStats(rs: RunConversation[]): RunStats {
  let flagged = 0, passed = 0, abort = 0, runsFlagged = 0, runsScored = 0;
  const lats: number[] = [], firsts: number[] = [];
  for (const run of rs) {
    let first: number | null = null, scored = false;
    run.rounds.forEach((r, i) => {
      if (r.outcome === "flagged") { flagged++; if (first == null) first = i + 1; }
      else if (r.outcome === "passed") passed++;
      else abort++;
      if (r.outcome !== "abort") scored = true;
      if (r.latencyMs != null) lats.push(r.latencyMs);
    });
    if (scored) runsScored++;
    if (first != null) { runsFlagged++; firsts.push(first); }
  }
  const scored = flagged + passed;
  return {
    runs: rs.length, flagged, passed, abort, scored,
    rate: scored ? flagged / scored : null,
    runRate: runsScored ? runsFlagged / runsScored : null,
    firstMed: quantile(firsts, 0.5), p50: quantile(lats, 0.5), p95: quantile(lats, 0.95),
  };
}

/** Flag rate for each turn number, per source (scored turns only). */
export function flagRateByTurn(rs: RunConversation[], source: string, maxTurns: number) {
  return Array.from({ length: maxTurns }, (_, i) => {
    let c = 0, n = 0;
    for (const run of rs) {
      const r = run.source === source ? run.rounds[i] : undefined;
      if (r && r.outcome !== "abort") { n++; if (r.outcome === "flagged") c++; }
    }
    return n ? { turn: i + 1, rate: c / n, c, n } : null;
  });
}

export const runStopLabel = (run: RunConversation) =>
  run.stop?.error ? "error: " + run.stop.error : (run.stop?.run_outcome ?? run.stop?.stopping_reason ?? "");

// ---------------------------------------------------------------- reviews

export const entryOf = (r: Pick<ConversationReview, "turns" | "overall">, turn: number | "overall"): ReviewEntry =>
  (turn === "overall" ? r.overall : r.turns?.[String(turn)]) || {};

export const reviewsFor = (reviews: ConversationReview[], conversationId: string) =>
  reviews.filter((r) => r.conversationId === conversationId && reviewHasContent(r));

export const reviewerName = (r: ConversationReview) => r.reviewerEmail || "Someone";

const hasOwnReview = (reviews: ConversationReview[], viewerId: string, conversationId: string) =>
  reviews.some((r) => r.conversationId === conversationId && r.userId === viewerId && reviewHasContent(r));

/** Server counts include the viewer's own review; strip it so the page can add it back as it changes. */
export function otherReviewCounts(serverCounts: Record<string, number> = {}, reviews: ConversationReview[], viewerId: string) {
  const out = { ...serverCounts };
  for (const id of Object.keys(out)) if (hasOwnReview(reviews, viewerId, id)) out[id] = Math.max(0, (out[id] ?? 0) - 1);
  return out;
}

export interface ReviewStatus {
  /** Reviewers of this conversation, counting everyone. */
  count: number;
  /** Whether the viewer has reviewed it (for tracking their own backlog). */
  mine: boolean;
}

/** Everyone else's count (from the server) plus the viewer's own review, if any. */
export function reviewStatusFor(others: Record<string, number>, reviews: ConversationReview[], viewerId: string, conversationId: string): ReviewStatus {
  const mine = hasOwnReview(reviews, viewerId, conversationId);
  return { count: (others[conversationId] ?? 0) + (mine ? 1 : 0), mine };
}

// ---------------------------------------------------------------- markdown report

const cell = (s: unknown) => String(s ?? "").replace(/\|/g, "\\|").replace(/\s*\n\s*/g, " ");
function mdTable(head: string[], rows: unknown[][]) {
  return `| ${head.join(" | ")} |\n|${head.map((_, i) => (i ? " ---: " : " --- ")).join("|")}|\n` +
    rows.map((r) => `| ${r.map(cell).join(" | ")} |`).join("\n");
}

export interface ReportInput {
  setName: string;
  scenarios: ScenarioConversation[]; // all scenarios in the set
  filteredScenarios: ScenarioConversation[];
  scenarioFilterLabel: string;
  runs: RunConversation[]; // runs matching the turn-log filters
  sources: string[]; // enabled sources, in display order
  reviews: ConversationReview[];
  /** "own" when the caller only received their own reviews (reviews are blind for non-super-admins). */
  reviewScope?: "all" | "own";
  now?: Date;
}

export function buildReport(input: ReportInput): string {
  const now = input.now ?? new Date();
  const out = [`# Multi-turn conversation report: ${input.setName}`, ``, `Generated ${now.toISOString().slice(0, 16).replace("T", " ")} UTC.`];

  if (input.scenarios.length) {
    const base = input.scenarios, { n, f } = failRate(base);
    const major = base.filter((s) => s.result === "Fail" && s.severity !== "minor").length;
    const filters = input.scenarioFilterLabel;
    out.push(``, `## Scenarios`, ``,
      `- **Scenarios:** ${n}`, `- **Held:** ${n - f} (${pct(n ? (n - f) / n : null, 1)})`,
      `- **Failures:** ${f} (${major} major, ${f - major} minor)`,
      `- **Harmful compliance:** ${base.filter((s) => s.mode === "harmful_compliance").length}`,
      `- **Infra errors:** ${base.filter((s) => s.infra).length}`, ``,
      `### Failure rate by BRI category`, ``,
      mdTable(["BRI category", "Failure rate", "Failed", "Scenarios"],
        categoryRates(base).map((c) => [`${catCode(c.cat)} ${c.name}`, pct(c.f / c.n), c.f, c.n])));
    const fails = input.filteredScenarios.filter((s) => s.result === "Fail");
    out.push(``, `### Failures${filters ? ` (filtered: ${filters})` : ""}`, ``);
    if (!fails.length) out.push(`No failures${filters ? " match these filters" : ""}.`);
    for (const s of fails) {
      out.push(`#### ${s.label} (\`${s.scenarioId}\`)`, ``,
        `- **BRI:** ${catCode(s.cat)} ${s.catName} · **Area:** ${s.area}`,
        `- **Failure mode:** ${s.mode} · **Severity:** ${s.severity || "major"}`,
        s.goal ? `- **Adversary goal:** ${cell(s.goal)}` : "", ``, `> ${cell(s.judge)}`, ``);
    }
  }

  if (input.runs.length) {
    out.push(``, `## Turn log`, ``,
      mdTable(["Source", "Turn flag rate", "Flagged / scored", "Runs flagged ≥1×", "Median first flag", "Infra errors"],
        input.sources.map((s) => {
          const st = runStats(input.runs.filter((r) => r.source === s));
          return [s, pct(st.rate), `${st.flagged} / ${st.scored}`, pct(st.runRate), st.firstMed == null ? "–" : "T" + st.firstMed, st.abort];
        })));
  }

  out.push(...feedbackReport(input));
  return out.filter((l, i, a) => !(l === "" && a[i - 1] === "")).join("\n") + "\n";
}

function feedbackReport(input: ReportInput): string[] {
  const all = [...input.scenarios, ...input.runs];
  const items = all.map((c) => [c, reviewsFor(input.reviews, c.id)] as const).filter(([, rv]) => rv.length);
  const ownOnlyNote = [``, `Only your own reviews are included (reviews are blind). A super admin can export everyone's.`];
  if (!items.length) {
    // Still explain why no reviews are listed when the viewer only sees their own and has none yet.
    return input.reviewScope === "own" ? [``, `## Reviewer feedback`, ...ownOnlyNote, ``, `You have not reviewed any conversations yet.`] : [];
  }
  const count = (entries: ReviewEntry[]) => REVIEW_RATINGS.map((r) => entries.filter((e) => e.rating === r).length).join(" / ");
  const bySource = new Map<string, (typeof items)[number][]>();
  for (const item of items) bySource.set(item[0].source, [...(bySource.get(item[0].source) || []), item]);

  const lines: string[] = [];
  for (const [c, rv] of items) {
    const title = c.kind === "scenario" ? `${c.label} (\`${c.scenarioId}\`)` : `${c.source} · ${c.attack} · ${c.category}`;
    for (const r of rv) {
      for (const t of ["overall", ...Object.keys(r.turns || {}).sort((a, b) => Number(a) - Number(b))]) {
        const e = entryOf(r, t === "overall" ? "overall" : Number(t));
        if (e.rating === "poor" || e.note) {
          lines.push(`- **${title}**, ${t === "overall" ? "all replies" : "T" + t + " reply"}, ${reviewerName(r)}: ` +
            `${e.rating ? REVIEW_RATING_LABELS[e.rating] : "no rating"}${e.note ? " · " + cell(e.note) : ""}`);
        }
      }
    }
  }
  return [``, `## Reviewer feedback`, ``,
    `Ratings of the persona's replies only (user messages are not reviewed), counted as Good / Acceptable / Poor.`,
    ...(input.reviewScope === "own" ? ownOnlyNote : []), ``,
    mdTable(["Model / file", "Conversations reviewed", "Reviews", "Turn ratings", "All-replies ratings"],
      [...bySource].map(([src, list]) => {
        const rv = list.flatMap(([, r]) => r);
        return [src, list.length, rv.length, count(rv.flatMap((r) => Object.values(r.turns || {}))), count(rv.map((r) => r.overall || {}))];
      })),
    ``, `### Poor ratings and notes`, ``, ...(lines.length ? lines : ["No poor ratings or notes yet."])];
}
